// Resume translations for multiple languages
export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' }
]

export const sectionLabels: Record<string, Record<string, string>> = {
  en: {
    professionalSummary: 'Professional Summary',
    workExperience: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    contact: 'Contact',
    present: 'Present'
  },
  es: {
    professionalSummary: 'Resumen Profesional',
    workExperience: 'Experiencia Laboral',
    education: 'Educación',
    skills: 'Habilidades',
    projects: 'Proyectos',
    certifications: 'Certificaciones',
    contact: 'Contacto',
    present: 'Presente'
  },
  fr: {
    professionalSummary: 'Résumé Professionnel',
    workExperience: 'Expérience Professionnelle',
    education: 'Formation',
    skills: 'Compétences',
    projects: 'Projets',
    certifications: 'Certifications',
    contact: 'Contact',
    present: 'Présent'
  },
  de: {
    professionalSummary: 'Berufliche Zusammenfassung',
    workExperience: 'Berufserfahrung',
    education: 'Ausbildung',
    skills: 'Fähigkeiten',
    projects: 'Projekte',
    certifications: 'Zertifizierungen',
    contact: 'Kontakt',
    present: 'Gegenwart'
  },
  pt: {
    professionalSummary: 'Resumo Profissional',
    workExperience: 'Experiência Profissional',
    education: 'Educação',
    skills: 'Habilidades',
    projects: 'Projetos',
    certifications: 'Certificações',
    contact: 'Contato',
    present: 'Presente'
  },
  zh: {
    professionalSummary: '专业总结',
    workExperience: '工作经验',
    education: '教育背景',
    skills: '技能',
    projects: '项目',
    certifications: '证书',
    contact: '联系方式',
    present: '至今'
  },
  ja: {
    professionalSummary: '職務経歴',
    workExperience: '職歴',
    education: '学歴',
    skills: 'スキル',
    projects: 'プロジェクト',
    certifications: '資格',
    contact: '連絡先',
    present: '現在'
  },
  ko: {
    professionalSummary: '전문 요약',
    workExperience: '경력',
    education: '학력',
    skills: '기술',
    projects: '프로젝트',
    certifications: '자격증',
    contact: '연락처',
    present: '현재'
  },
  ar: {
    professionalSummary: 'الملخص المهني',
    workExperience: 'الخبرة العملية',
    education: 'التعليم',
    skills: 'المهارات',
    projects: 'المشاريع',
    certifications: 'الشهادات',
    contact: 'اتصل',
    present: 'الحاضر'
  },
  hi: {
    professionalSummary: 'पेशेवर सारांश',
    workExperience: 'कार्य अनुभव',
    education: 'शिक्षा',
    skills: 'कौशल',
    projects: 'परियोजनाएं',
    certifications: 'प्रमाणपत्र',
    contact: 'संपर्क',
    present: 'वर्तमान'
  }
}

// Translation using Google Translate-like free API
export async function translateText(text: string, targetLanguage: string): Promise<string> {
  if (!text || targetLanguage === 'en') {
    return text
  }

  try {
    // Using MyMemory Translation API (free, no API key required)
    const response = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLanguage}`
    )
    const data = await response.json()

    if (data.responseStatus === 200 && data.responseData?.translatedText) {
      return data.responseData.translatedText
    }

    return text
  } catch (error) {
    console.error('Translation error:', error)
    return text
  }
}

// Translate entire resume data object
export async function translateResumeData(resumeData: any, targetLanguage: string): Promise<any> {
  if (targetLanguage === 'en') {
    return resumeData
  }

  try {
    const translated = { ...resumeData }

    // Translate summary
    if (resumeData.summary) {
      translated.summary = await translateText(resumeData.summary, targetLanguage)
    }

    // Translate experience
    if (resumeData.experience && Array.isArray(resumeData.experience)) {
      translated.experience = await Promise.all(
        resumeData.experience.map(async (exp: any) => ({
          ...exp,
          jobTitle: await translateText(exp.jobTitle, targetLanguage),
          company: exp.company, // Keep company names unchanged
          location: exp.location, // Keep locations unchanged
          description: await translateText(exp.description, targetLanguage)
        }))
      )
    }

    // Translate education
    if (resumeData.education && Array.isArray(resumeData.education)) {
      translated.education = await Promise.all(
        resumeData.education.map(async (edu: any) => ({
          ...edu,
          degree: await translateText(edu.degree, targetLanguage),
          institution: edu.institution, // Keep institution names unchanged
          location: edu.location
        }))
      )
    }

    // Translate projects
    if (resumeData.projects && Array.isArray(resumeData.projects)) {
      translated.projects = await Promise.all(
        resumeData.projects.map(async (proj: any) => ({
          ...proj,
          name: await translateText(proj.name, targetLanguage),
          description: await translateText(proj.description, targetLanguage),
          technologies: proj.technologies // Keep tech stack unchanged
        }))
      )
    }

    // Translate certifications
    if (resumeData.certifications && Array.isArray(resumeData.certifications)) {
      translated.certifications = await Promise.all(
        resumeData.certifications.map(async (cert: any) => ({
          ...cert,
          name: await translateText(cert.name, targetLanguage),
          issuer: cert.issuer // Keep issuer names unchanged
        }))
      )
    }

    // Skills and personal info remain unchanged
    translated.skills = resumeData.skills
    translated.personalInfo = resumeData.personalInfo

    return translated
  } catch (error) {
    console.error('Resume translation error:', error)
    return resumeData
  }
}

export function getTranslatedLabels(languageCode: string): Record<string, string> {
  return sectionLabels[languageCode] || sectionLabels.en
}
